import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/shared/ui";
import { LanguageSwitcher } from "../LanguageSwitcher";
import { ROUTES } from "@/shared/core";

export const Navbar: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeHash, setActiveHash] = useState("");
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Detectar el hash activo desde la URL
    const hash = window.location.hash || "#home";
    setActiveHash(hash);

    // Escuchar cambios en el hash
    const handleHashChange = () => {
      setActiveHash(window.location.hash || "#home");
    };

    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, [location]);

  // Prevenir scroll del body cuando el menú está abierto
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMenuOpen]);

  const navLinks = [
    { label: t("nav.home"), href: "#home" },
    { label: t("nav.lawyers"), path: "/lawyers" },
    { label: t("nav.features"), href: "#features" },
    { label: t("nav.pricing"), href: "#pricing" },
    { label: t("nav.faq"), href: "#faq" },
    // { label: t("nav.testimonials"), href: "#testimonials" },
    { label: t("nav.contact"), href: "#contact" },
  ];

  return (
    <nav className="bg-tiber-900 text-romance-50 sticky top-0 z-50 overflow-x-hidden">
      <div className="container mx-auto px-3 sm:px-6 lg:px-8 max-w-full min-w-0">
        <div className="flex items-center justify-between h-14 sm:h-16 md:h-20 gap-2 min-w-0">
          {/* Logo */}
          <div className="flex items-center cursor-pointer shrink-0">
            <img
              src="/images/brand/lawgentia-logo-white.png"
              className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16"
              alt="Lawgentia Logo"
              draggable={false}
            />
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navLinks.map((link, index) => {
              const isActive = "href" in link && activeHash === link.href;
              const linkClass = `relative text-romance-50 font-medium text-base transition-colors duration-300 ${
                isActive ? "after:w-full" : "after:w-0"
              } after:absolute after:bottom-0 after:left-0 after:h-0.5 after:bg-green-200 after:transition-all after:duration-300 hover:after:w-full`;
              if ("path" in link && link.path) {
                return (
                  <button
                    key={index}
                    type="button"
                    onClick={() => navigate(link.path)}
                    className={linkClass}
                  >
                    {link.label}
                  </button>
                );
              }
              return (
                <a key={index} href={link.href} className={linkClass}>
                  {link.label}
                </a>
              );
            })}
          </div>

          {/* Desktop CTA + Language */}
          <div className="hidden lg:flex items-center space-x-3">
            <LanguageSwitcher />
            <Button
              variant="ghost"
              size="md"
              onClick={() => navigate(ROUTES.AUTH)}
              className="bg-white"
            >
              {t("nav.signIn")}
            </Button>
            <Button
              variant="primary"
              size="md"
              onClick={() => navigate("/onboarding/select-type")}
              className="solid border-2 border-romance-50 text-romance-50"
            >
              {t("nav.getStarted")}
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-juridico p-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            <svg
              className="w-6 h-6 text-white"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              {isMenuOpen ? (
                <path d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 z-50 flex"
          onClick={() => setIsMenuOpen(false)}
        >
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 transition-opacity duration-300"
            onClick={() => setIsMenuOpen(false)}
          />

          {/* Drawer from right */}
          <div
            className="fixed right-0 top-0 h-full w-72 sm:w-80 max-w-[90vw] bg-tiber-900 shadow-xl transform transition-transform duration-300 ease-in-out overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col h-full min-h-0">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-tiber-700 shrink-0">
                <div className="flex items-center shrink-0">
                  <img
                    src="/images/brand/lawgentia-logo-white.png"
                    className="w-14 h-14 sm:w-16 sm:h-16"
                    alt="Lawgentia Logo"
                    draggable={false}
                  />
                </div>
                <button
                  className="text-juridico p-2 hover:text-white transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                  aria-label="Close menu"
                >
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Menu Items */}
              <div className="flex flex-col py-6 px-4 space-y-1 overflow-y-auto flex-1 min-h-0">
                {navLinks.map((link, index) => {
                  const isActive = "href" in link && activeHash === link.href;
                  const linkClass = `relative text-romance-50 font-medium text-base transition-colors duration-300 px-4 py-3 text-left ${
                    isActive ? "after:w-full" : "after:w-0"
                  } after:absolute after:bottom-2 after:left-4 after:h-0.5 after:bg-green-200 after:transition-all after:duration-300 hover:after:w-[calc(100%-2rem)] hover:text-tiber-500`;
                  if ("path" in link && link.path) {
                    return (
                      <button
                        key={index}
                        type="button"
                        onClick={() => {
                          setIsMenuOpen(false);
                          navigate(link.path);
                        }}
                        className={linkClass}
                      >
                        {link.label}
                      </button>
                    );
                  }
                  return (
                    <a
                      key={index}
                      href={link.href}
                      className={linkClass}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {link.label}
                    </a>
                  );
                })}
              </div>

              {/* Footer Actions */}
              <div className="mt-auto p-4 border-t border-tiber-700 space-y-3 shrink-0">
                <div className="flex justify-center py-2">
                  <LanguageSwitcher className="w-full" />
                </div>
                <Button
                  variant="secondary"
                  size="md"
                  fullWidth
                  className="border-white text-white hover:text-white"
                  onClick={() => {
                    setIsMenuOpen(false);
                    navigate(ROUTES.AUTH);
                  }}
                >
                  {t("nav.signIn")}
                </Button>
                <Button
                  variant="secondary"
                  size="md"
                  fullWidth
                  onClick={() => {
                    setIsMenuOpen(false);
                    navigate("/onboarding/select-type");
                  }}
                  className="border-tiber-500 text-tiber-500 hover:bg-tiber-500 hover:text-tiber-900"
                >
                  {t("nav.getStarted")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};
